import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { boardsAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Play, Globe, Lock, Share2 } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function BoardEdit() {
  const { boardId } = useParams();
  useSEO('Board Details | JepPrep', 'View and manage your trivia board details, categories, and sharing options.');
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBoard();
  }, [boardId]);

  const loadBoard = async () => {
    try {
      const response = await boardsAPI.get(boardId);
      setBoard(response.data);
    } catch (error) {
      toast.error('Failed to load board');
      navigate('/boards');
    } finally {
      setLoading(false);
    }
  };

  const startGame = () => {
    navigate(`/boards/${boardId}/play`, { state: { board } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading board...</div>
      </div>
    );
  }

  if (!board) return null;

  const isShared = board.community_challenge;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/boards">
                <Button variant="ghost" size="sm" className="text-gray-300 px-2 sm:px-3">
                  <ArrowLeft className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-8 sm:h-12 hidden sm:block" />
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              {isShared && (
                <Link to={`/community/${boardId}`}>
                  <Button variant="outline" size="sm" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 text-xs sm:text-sm px-2 sm:px-3" data-testid="view-community-btn">
                    <Share2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                    <span className="hidden sm:inline">View on Community</span>
                    <span className="sm:hidden">Community Boards</span>
                  </Button>
                </Link>
              )}
              <Button 
                onClick={startGame}
                disabled={!board.categories || board.categories.length < 6}
                className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-xs sm:text-sm px-2 sm:px-4"
                data-testid="start-game-button"
              >
                <Play className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Play Board</span>
                <span className="sm:hidden ml-1">Play</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Board Info */}
        <div className="mb-6">
          <div className="flex items-start gap-3 mb-2">
            <h1 className="font-display font-bold text-xl sm:text-2xl text-white">{board.title}</h1>
            <span className={`px-2 py-1 rounded text-xs shrink-0 ${board.visibility === 'public' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
              {board.visibility === 'public' ? <><Globe className="w-3 h-3 inline mr-1" />Public</> : <><Lock className="w-3 h-3 inline mr-1" />Private</>}
            </span>
          </div>
          <p className="text-gray-400 text-sm sm:text-base">{board.description || 'No description'}</p>
          <p className="text-gray-500 text-sm mt-1">Difficulty: {board.difficulty} • {board.categories?.length || 0} categories</p>
        </div>

        {/* Board Preview */}
        {board.categories && board.categories.length > 0 ? (
          <div className="space-y-6">
            {/* Mobile: Category tiles */}
            <div className="md:hidden grid grid-cols-2 gap-3" data-testid="mobile-board-preview">
              {board.categories.map((category, idx) => (
                <div key={category.name || `cat-mobile-${idx}`} className="bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--brand-gold-500))]/20 rounded-xl p-4 text-center">
                  <p className="text-[hsl(var(--brand-gold-500))] font-bold text-sm uppercase leading-tight" data-testid={`preview-cat-${idx}`}>{category.name}</p>
                  <p className="text-gray-500 text-xs mt-1">{category.clues?.length || 5} clues</p>
                </div>
              ))}
            </div>

            {/* Desktop: Full grid */}
            <div className="hidden md:block space-y-6">
              {/* Category Headers */}
              <div className="grid grid-cols-6 gap-2">
                {board.categories.map((category, idx) => (
                  <div key={category.name || `cat-desk-${idx}`} className="category-header text-center py-3 text-xs sm:text-sm">
                    {category.name}
                  </div>
                ))}
              </div>

              {/* Clue Grid */}
              {[0, 1, 2, 3, 4].map((row) => (
                <div key={row} className="grid grid-cols-6 gap-2">
                  {board.categories.map((category, catIdx) => {
                    const clue = category.clues?.[row];
                    const value = [100, 200, 300, 400, 500][row];
                    return (
                      <div
                        key={catIdx}
                        className="board-cell text-sm sm:text-xl"
                        data-testid={`board-cell-${catIdx}-${row}`}
                      >
                        ${value}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Final Jeopardy */}
            {board.final_jeopardy && (
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] mt-8">
                <CardHeader>
                  <CardTitle className="text-[hsl(var(--brand-gold-500))]">Final Jeopardy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">Category: {board.final_jeopardy.category}</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-12 text-center">
              <p className="text-gray-400 mb-4">This board doesn't have any categories yet.</p>
              <p className="text-gray-500 text-sm">Generate a new board or add categories manually.</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
